import { z } from 'zod';
import { EnrollRequest } from './agent-api.js';
import { IpAddress, IsoTime, ServerId } from './basics.js';
import { ServerDetail, ServerGroupId } from './server.js';
import { Role } from './settings.js';
/**
 * Pairing (knowledge doc section 10, method B; section 15).
 *
 * The fallback for a single server, where method A's fleet-wide token is more
 * machinery than the job needs: the agent asks to join, the API issues a one-time
 * code, the terminal prints it beside the hostname, address and key fingerprint,
 * and an admin types the code into React and checks that those three match.
 *
 * Both halves live in one file even though one is an agent route and the other a
 * dashboard route. They are not two contracts: the code the terminal prints is the
 * code the admin types, and the fingerprint React shows has to be the same string
 * the terminal did, character for character, or comparing them proves nothing.
 * Splitting them across `agent-api.ts` and a dashboard file is how the two ends
 * drift.
 */
/**
 * Section 10: "readable alphabet without look-alike characters".
 *
 * Crockford's base32 without `0` and `1` either, so the whole set of pairs anyone
 * confuses reading a terminal aloud — `0`/`O`, `1`/`I`/`l`, `U`/`V` — is simply
 * absent. Thirty characters, which is not a power of two: generating from it needs
 * rejection sampling rather than a modulo, and that is the generator's problem.
 */
export const PAIRING_CODE_ALPHABET = '23456789ABCDEFGHJKMNPQRSTVWXYZ';
/**
 * Eight characters, printed as two groups of four.
 *
 * About 39 bits. Far below an enrollment token's 256, and deliberately so: this is
 * typed by a person from a screen. What keeps it safe is not its length but its
 * fifteen minutes, its single use, and the fact that only an admin can spend it —
 * guessing it online means guessing past a per-address limit on a route that
 * already requires a session.
 */
export const PAIRING_CODE_LENGTH = 8;
export const PAIRING_CODE_GROUP_SIZE = 4;
/** Section 10: "about 15 minutes". */
export const PAIRING_TTL_MINUTES = 15;
/**
 * How long `GET /pairing/:id?wait=true` parks before answering.
 *
 * Under the agent client's 30 s request timeout, and long enough that waiting out
 * the full fifteen minutes is about thirty requests rather than several hundred.
 */
export const PAIRING_POLL_HOLD_MS = 25_000;
const CODE_PATTERN = new RegExp(`^[${PAIRING_CODE_ALPHABET}]{${String(PAIRING_CODE_LENGTH)}}$`);
/**
 * What a person typed, as the API stores it.
 *
 * Case and the separator are presentation: `abcd-efgh`, `ABCDEFGH` and
 * `abcd efgh` are one code. Nothing else is forgiven — a typed `O` or `0` is not
 * in the alphabet at all, so it is a mistake with no valid reading behind it.
 */
export const normalizePairingCode = (input) => input.replace(/[\s-]+/g, '').toUpperCase();
/** `ABCD-EFGH`: what the terminal prints and the approval screen shows. */
export const formatPairingCode = (code) => {
    const normalized = normalizePairingCode(code);
    const groups = [];
    for (let i = 0; i < normalized.length; i += PAIRING_CODE_GROUP_SIZE) {
        groups.push(normalized.slice(i, i + PAIRING_CODE_GROUP_SIZE));
    }
    return groups.join('-');
};
export const PairingCode = z
    .string()
    .min(PAIRING_CODE_LENGTH)
    .max(64)
    .transform(normalizePairingCode)
    .refine((code) => CODE_PATTERN.test(code), 'not a pairing code');
export const PairingRequestId = z.uuid();
/**
 * `SHA256:<base64>` over the SPKI DER, which is what the agent prints and what
 * OpenSSH's key fingerprints look like — a form an administrator already reads.
 */
export const KeyFingerprint = z.string().regex(/^SHA256:[A-Za-z0-9+/]{43}=$/, 'not a fingerprint');
/**
 * The same machine as method A describes, without the token.
 *
 * Derived from `EnrollRequest` rather than restated, so a field added to one
 * method's description of a server cannot go missing from the other's.
 */
export const PairingStartRequest = EnrollRequest.omit({ token: true });
/**
 * Two answers, because running the installer twice is normal and must not produce
 * a second server. A key the API already knows gets its own id back and no code at
 * all; there is nothing for an admin to approve.
 */
export const PairingStartResponse = z.discriminatedUnion('state', [
    z.object({
        state: z.literal('code_issued'),
        requestId: PairingRequestId,
        /** The plaintext, returned once. The API keeps only its hash. */
        code: z.string().min(PAIRING_CODE_LENGTH).max(32),
        /** Computed by the API from the key it stored, so both ends show one string. */
        fingerprint: KeyFingerprint,
        expiresAt: IsoTime,
    }),
    z.object({
        state: z.literal('already_joined'),
        serverId: ServerId,
        status: z.enum(['active', 'pending']),
    }),
]);
/**
 * Whether a request is still waiting, worked out from the row and the clock rather
 * than stored — the same reasoning as an enrollment token's state. A swept-away
 * request and one that has run out of time are the same answer to the agent.
 * `denied` is an admin's refusal (P8.4), told to the agent so it stops waiting and
 * says so rather than that its code ran out.
 */
export const PairingState = z.enum(['pending', 'approved', 'expired', 'denied']);
/**
 * `GET /pairing/:id` (section 15: "Agent waits for approval").
 *
 * Public, because the agent still has no credential — so it carries nothing worth
 * knowing. The id is a UUID the API chose, and a server id is an identity rather
 * than a capability: holding one without the private key lets nobody do anything.
 * The hostname, the address and the fingerprint stay on the admin's side.
 */
export const PairingPollResponse = z.object({
    state: PairingState,
    /** Set once an admin has approved it; the agent saves this as its identity. */
    serverId: ServerId.nullable(),
    expiresAt: IsoTime,
});
/** What the approval screen shows so an admin can check it against the terminal. */
export const PairingRequestSummary = z.object({
    id: PairingRequestId,
    hostname: z.string().min(1).max(255),
    /**
     * The address the server reported about itself, which is what its terminal
     * printed — so it is the one to compare. It is self-reported and decides nothing.
     */
    ip: IpAddress.nullable(),
    /**
     * The address the request actually arrived from. Shown beside the reported one
     * because they disagree for a good reason (NAT) and for a bad one, and only the
     * person approving can tell which.
     */
    peerIp: IpAddress.nullable(),
    os: z.string().max(100).nullable(),
    detectedRoles: z.array(Role),
    agentVersion: z.string().max(50).nullable(),
    fingerprint: KeyFingerprint,
    requestedAt: IsoTime,
    expiresAt: IsoTime,
    state: PairingState,
});
/**
 * Looking a code up before approving it (section 10: "checks the details, and
 * approves" — two steps, and the first one is why the code is not simply spent).
 *
 * A POST, not a query parameter. The code is a secret with a short life, and a
 * query string is written to access logs, proxy logs and browser history, which is
 * rule 5's "never in logs" applied to a URL.
 */
export const LookupPairingCodeRequest = z.object({ code: PairingCode });
/**
 * Approving takes the code again.
 *
 * The listing shows what is waiting, but admitting a machine to the fleet needs
 * the code from its terminal — which is what makes approval a statement about the
 * server in front of you rather than about whichever request happened to be
 * pending. Without it, a request from anywhere could be approved by an admin who
 * was expecting a different one.
 */
export const ApprovePairingRequest = z.object({
    code: PairingCode,
    /**
     * Method A carries the group on the token. Method B has no token, so the person
     * approving is the only place this can come from.
     */
    groupId: ServerGroupId.nullable().default(null),
});
export const ApprovePairingResponse = z.object({ server: ServerDetail });
//# sourceMappingURL=pairing.js.map